import type { ActedForView, OthersRow, WaitingOnOthers } from '@myrewards/contracts';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { doneByMeQuery } from '../../api/actingFor';
import { Pair } from '../../acting-for/parts';
import {
  othersMetaLine,
  othersPill,
  othersTypeLine,
  sectionsOn,
  whenWords,
  type OthersSection,
  othersIntro,
  othersNothingWords,
  switchedOffWords,
} from '../../acting-for/words';
import { keepIdsWhole } from '../../shell/Hero';
import {
  ButtonLink,
  Card,
  cx,
  EmptyState,
  formatCount,
  formatRecentTime,
  Icon,
  plural,
  StatusPill,
} from '../../ui';
import o from './Others.module.css';

// APP-148 D2: Approvals for a Stock Admin is "Waiting on others": the Executives' decisions and
// the Sector Leads' work, each section oldest first, then what the reader did for others in the
// last 30 days (Queue board). Each section has its own empty state (Queue and Empty boards).

/**
 * One row as the Queue board draws it (R46): the award's ID, whose it is and any pool over budget
 * in words, its type, people and amount, then who entered it, what it's for, why it waits and for
 * how long. Open goes to the page that decides it.
 */
function OthersItem({ row, asOf }: { row: OthersRow; asOf: string }): ReactElement {
  return (
    <li className={o.row}>
      <div className={o.rowText}>
        <div className={o.rowHead}>
          <h3 className={cx('disp', o.rowRef)}>{keepIdsWhole(row.ref)}</h3>
          <StatusPill tone="waiting" size="sm" label={othersPill(row.state)} />
          {row.overPools.map((p) => (
            <StatusPill key={p.pool} tone="problem" size="sm" label={`${p.pool} over budget`} />
          ))}
        </div>
        <p className={o.rowType}>{othersTypeLine(row)}</p>
        <p className={o.rowDetail}>{othersMetaLine(row, asOf)}</p>
      </div>
      <ButtonLink href={row.href} variant="navy" aria-label={`Open ${row.ref}`}>
        Open
      </ButtonLink>
    </li>
  );
}

function OthersCard({
  section,
  rows,
  asOf,
}: {
  section: OthersSection;
  rows: readonly OthersRow[];
  asOf: string;
}): ReactElement {
  return (
    <Card
      title={`${section.title} · ${formatCount(rows.length)}`}
      help={section.help}
      {...(rows.length > 0 && { aside: <span className={o.rowDetail}>Oldest first</span> })}
    >
      {rows.length === 0 ? (
        <p className={o.calm}>
          <Icon name="check" size={16} />
          <span>{section.empty}</span>
        </p>
      ) : (
        <ul className={o.list}>
          {rows.map((row) => (
            <OthersItem key={row.key} row={row} asOf={asOf} />
          ))}
        </ul>
      )}
    </Card>
  );
}

/** "Last done: SDB-2026-0157, today at 11:24 AM, by Meredith Okonkwo for Graham Whitlock." */
function lastDoneWords(view: ActedForView | null, asOf: string): string | null {
  if (view === null) return null;
  const what = view.award?.displayId;
  return `Last done: ${what === null || what === undefined ? '' : `${what}, `}${whenWords(view.at, asOf)}, by ${view.actor.name} for ${view.for.name}.`;
}

function DoneItem({ view }: { view: ActedForView }): ReactElement {
  const title = [view.award?.displayId ?? null, view.line]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');
  const files =
    view.files.length === 0 ? null : `${plural(view.files.length, 'file', 'files')} kept with it`;
  return (
    <li className={o.row}>
      <Pair actor={view.actor.name} person={view.for.name} />
      <div className={o.rowText}>
        <h3 className={o.rowTitle}>{keepIdsWhole(title)}</h3>
        <p className={o.rowDetail}>
          {[formatRecentTime(view.at), view.detail, view.detail === null ? files : null]
            .filter((part): part is string => part !== null && part !== '')
            .join(' · ')}
        </p>
        {view.note !== null && view.note.trim() !== '' && (
          <p className={o.rowNote}>Your note: “{view.note}”</p>
        )}
      </div>
      {view.award !== null && (
        <ButtonLink href={`/awards/${view.award.id}`} variant="ghost" size="sm">
          Open
        </ButtonLink>
      )}
    </li>
  );
}

/** What you did for others · last 30 days: the reader's own, newest first. */
function DoneByMeCard(): ReactElement {
  const done = useQuery({ ...doneByMeQuery(30), retry: false });
  const items = done.data?.items ?? [];
  const total = done.data?.page.total ?? 0;
  return (
    <Card
      title="What you did for others · last 30 days"
      help={{
        shows:
          'What you did for an Executive or a Sector Lead in the last 30 days, newest first, with your note and any files.',
        canDo:
          'Open one to see the award. The person it was for sees the same list on their Home, and can say if it isn’t what they asked for.',
      }}
    >
      {items.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="people"
          size="sm"
          headline="Nothing yet"
          body="When you do something for an Executive or a Sector Lead, it shows here with your note and any files. They see the same list on their Home."
        />
      ) : (
        <>
          <ul className={o.list}>
            {items.map((view) => (
              <DoneItem key={view.id} view={view} />
            ))}
          </ul>
          {total > items.length && (
            <p className={o.more}>
              The newest {formatCount(items.length)} of {formatCount(total)} are shown.
            </p>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * One kind of the Executives' decisions, as the Executive's kind switch picks it (`?kind=`): its
 * section alone, and in it only the rows waiting on that decision (each row's own `decision`,
 * R68), under the section's title or its own.
 */
export interface OthersKind {
  section: OthersRow['section'];
  decision: OthersRow['decision'];
  title?: string;
  empty?: string;
}

/**
 * Waiting on others, with the sections the switches leave on. With nothing waiting in any of
 * them, one card says the Executives and Sector Leads are caught up (Empty board), with the last
 * thing anyone did for someone. What the reader did for others always follows. With a `kind`,
 * only that kind's section, with its own empty state.
 */
export function OthersSections({
  others,
  asOf,
  skip,
  kind = null,
}: {
  others: WaitingOnOthers;
  asOf: string;
  /** Rows the reader decides in their own right, already listed above (`award:<id>` keys). */
  skip: ReadonlySet<string>;
  kind?: OthersKind | null;
}): ReactElement {
  const sections = sectionsOn(others.switches);
  const rows = others.rows.filter(
    (row) => !skip.has(row.key) && sections.some((section) => section.id === row.section),
  );
  const only = kind === null ? undefined : sections.find((x) => x.id === kind.section);
  if (kind !== null && only !== undefined)
    return (
      <OthersCard
        section={{
          ...only,
          ...(kind.title !== undefined && { title: kind.title }),
          ...(kind.empty !== undefined && { empty: kind.empty }),
        }}
        rows={rows.filter((row) => row.decision === kind.decision)}
        asOf={asOf}
      />
    );
  // R142: with a switch off it says what isn't listed, never that a role is caught up.
  const nothing = othersNothingWords(others.switches);
  const nothingWords = {
    headline: nothing.headline,
    body: [nothing.body, lastDoneWords(others.lastDone, asOf)]
      .filter((part): part is string => part !== null)
      .join(' '),
  };
  const switchedOff = switchedOffWords(others.switches) !== null;
  return (
    <>
      {rows.length === 0 ? (
        <Card
          title="Waiting on others · 0"
          help={{
            shows: switchedOff
              ? othersIntro(others.switches)
              : 'The Executives’ decisions and the Sector Leads’ work, oldest first. Nothing is waiting on them now.',
            canDo:
              'Nothing to do here. When one of them asks you to do something for them, it shows here first.',
          }}
        >
          {switchedOff ? (
            // R149: a switch off isn't caught up. The plain informational look: never the
            // inbox-done art or the gold check beside "turned off".
            <EmptyState variant="first-day" art="people" size="sm" {...nothingWords} />
          ) : (
            <EmptyState variant="caught-up" size="sm" {...nothingWords} />
          )}
        </Card>
      ) : (
        sections.map((section) => (
          <OthersCard
            key={section.id}
            section={section}
            rows={rows.filter((row) => row.section === section.id)}
            asOf={asOf}
          />
        ))
      )}
      <DoneByMeCard />
    </>
  );
}
